import * as React from 'react'
import dynamic from 'next/dynamic'

import { i18n } from '@thg-commerce/enterprise-core'
import { Image } from '@thg-commerce/gravity-elements'

import {
  Content,
  HighlightText,
  ItemTitle,
  PlayButton,
  PreviewImageContainer,
  StyledButton,
  StyledSocialEmbed,
} from './styles'

const PlayIcon = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/PlayArrow'),
)

export interface VideoGalleryItemProps {
  previewImage: {
    url: {
      large: string
    }
    alt?: string
  }
  highlightText?: string
  title?: string
  content?: string
  buttonText?: string
  buttonLink?: string
  videoUrl: string
}

export interface VideoGalleryItemVideoProps {
  playVideo: (itemContent: any) => void
  itemId: number
  displayVideoOnMobile?: boolean
  windowWidth: number
  isOneColumn?: boolean
}

export const VideoGalleryItem = (
  item: VideoGalleryItemProps & VideoGalleryItemVideoProps,
) => {
  const [displayVideo, setDisplayVideo] = React.useState(false)

  const playVideoI18nText = i18n('general.play.video.button.text')

  React.useEffect(() => {
    if (item.displayVideoOnMobile) {
      setDisplayVideo(true)
    } else if (!item.isOneColumn && item.windowWidth > 600) {
      setDisplayVideo(false)
    }
  }, [item.displayVideoOnMobile, item.windowWidth, item.isOneColumn])

  return (
    <React.Fragment>
      <PreviewImageContainer hideImage={displayVideo}>
        <Image
          src={[{ url: item.previewImage.url.large }]}
          alt={item.previewImage.alt || ''}
          width="100%"
          height="100%"
          isAmp={false}
        />
        <PlayButton
          onClick={() =>
            item.playVideo({
              title: item.title,
              videoUrl: item.videoUrl,
              id: item.itemId,
            })
          }
          emphasis="high"
          data-testid="video-gallery-play-button"
          aria-label={`${playVideoI18nText} ${item.title || ''}`}
        >
          <PlayIcon />
          {playVideoI18nText}
        </PlayButton>
      </PreviewImageContainer>

      {displayVideo && (
        <div data-testid={`video-gallery-video-component-${item.itemId}`}>
          <StyledSocialEmbed
            url={item.videoUrl}
            provider="youtube"
            isAmp={false}
          />
        </div>
      )}
      {item.highlightText && (
        <HighlightText>{item.highlightText}</HighlightText>
      )}
      {item.title && <ItemTitle>{item.title}</ItemTitle>}
      {item.content && <Content>{item.content}</Content>}

      {item.buttonText && item.buttonLink && (
        <StyledButton
          emphasis="medium"
          renderedAs="a"
          href={item.buttonLink}
          aria-label={`${item.buttonText} ${item.title || ''}`}
        >
          {item.buttonText}
        </StyledButton>
      )}
    </React.Fragment>
  )
}
